import type {AnyObject} from 'typings/portaro.fe.types';
import type {DelayedReturnPenalty, UserDetailView, UsersView} from './types';
import type {ActionResponse, FinishedActionResponse, FinishedSaveResponse, RelationsRequest, UploadedUserFilesRequest, User, UserEditationRequest} from 'typings/portaro.be.types';
import type {AjaxService} from 'core/data-services/ajax.service';
import {transferify} from 'shared/utils/data-service-utils';
import {ngAsync} from 'shared/utils/ng-@decorators';
import {exists} from 'shared/utils/custom-utils';

export default class UserDataService {
    public static readonly serviceName = 'userDataService';

    public static readonly USERS_ROUTE = 'users';
    public static readonly PASSWORD_CHANGE_ROUTE = 'users/password-change';
    public static readonly TOKENED_PASSWORD_CHANGE_MAIL_ROUTE = 'users/tokened-password-change-mail';
    public static readonly SEQUENCES_CARD_NUMBER_ROUTE = 'users/sequences/card-number';
    public static readonly SEQUENCES_BAR_CODE_ROUTE = 'users/sequences/bar-code';
    public static readonly RELATIONS_ROUTE = 'users/relations';
    public static readonly CREDENTIALS_REGISTRATION_FIRST_LOGIN_ROUTE = 'users/credentials-registration/first-login';
    public static readonly UPLOAD_USER_FILES = 'users/upload-user-files';

    /*@ngInject*/
    constructor(private ajaxService: AjaxService) {
    }

    @ngAsync()
    public async saveUser(userEditationRequest: UserEditationRequest): Promise<FinishedSaveResponse<User>> {
        return this.ajaxService
            .createRequest(UserDataService.USERS_ROUTE)
            .post(transferify(userEditationRequest));
    }

    @ngAsync()
    public async editUserByForm(userId: number): Promise<FinishedSaveResponse<User>> {
        return this.ajaxService
            .createRequest(`${UserDataService.USERS_ROUTE}/${userId}/form`)
            .get();
    }

    @ngAsync()
    public async editSingleIsic(userId: number): Promise<FinishedSaveResponse<User>> {
        return this.ajaxService
            .createRequest(`${UserDataService.USERS_ROUTE}/edit/single/isic`)
            .post({user: userId});
    }

    @ngAsync()
    public async getUser(userId: number): Promise<User> {
        return this.ajaxService
            .createRequest(`${UserDataService.USERS_ROUTE}/${userId}`)
            .get();
    }

    @ngAsync()
    public async removeUser(userId: number): Promise<ActionResponse> {
        return this.ajaxService
            .createRequest(`${UserDataService.USERS_ROUTE}/${userId}`)
            .withBody({user: userId})
            .delete();
    }

    @ngAsync()
    public async getUsersView(): Promise<UsersView> {
        return this.ajaxService
            .createRequest(`${UserDataService.USERS_ROUTE}/users-view`)
            .get();
    }

    @ngAsync()
    public async getUserDetailView(userId: number): Promise<UserDetailView> {
        return this.ajaxService
            .createRequest(`${UserDataService.USERS_ROUTE}/${userId}/detail-view`)
            .get();
    }

    @ngAsync()
    public async extendRegistration(userId: number): Promise<FinishedActionResponse> {
        return this.ajaxService
            .createRequest(`${UserDataService.USERS_ROUTE}/registration-extend`)
            .post({extendingUser: userId});
    }

    @ngAsync()
    public async deleteUserCredentials(userId: number): Promise<ActionResponse> {
        return this.ajaxService
            .createRequest(`${UserDataService.USERS_ROUTE}/${userId}/credentials-delete`)
            .get();
    }

    @ngAsync()
    public async deleteLinkWithMojeID(userId: number): Promise<ActionResponse> {
        return this.ajaxService
            .createRequest('/mojeid/delete-link')
            .post({
                user: userId
            });
    }

    @ngAsync()
    public async deleteLinkWithBankID(userId: number): Promise<ActionResponse> {
        return this.ajaxService
            .createRequest('/bankid/delete-link')
            .post({
                user: userId
            });
    }

    @ngAsync()
    public async changePassword(formObject): Promise<FinishedActionResponse> {
        return this.ajaxService
            .createRequest(`${UserDataService.PASSWORD_CHANGE_ROUTE}`)
            .post(transferify(formObject));
    }

    @ngAsync()
    public async changePasswordMail(): Promise<FinishedActionResponse> {
        return this.ajaxService
            .createRequest(`${UserDataService.TOKENED_PASSWORD_CHANGE_MAIL_ROUTE}`)
            .post({});
    }

    @ngAsync()
    public async getDelayedReturnPenalty(userId: number): Promise<DelayedReturnPenalty> {
        return this.ajaxService
            .createRequest(`${UserDataService.USERS_ROUTE}/${userId}/delayed-return-penalty`)
            .get();
    }

    @ngAsync()
    public async getCardNumberSequence(departmentsIds: number[]): Promise<string> {
        return this.ajaxService
            .createRequest(`${UserDataService.SEQUENCES_CARD_NUMBER_ROUTE}`)
            .get({departments: departmentsIds});
    }

    @ngAsync()
    public async getReaderBarCodeSequence(departmentId: number): Promise<string> {
        return this.ajaxService
            .createRequest(`${UserDataService.SEQUENCES_BAR_CODE_ROUTE}`)
            .get({department: departmentId});
    }

    @ngAsync()
    public async saveRelations(relationsRequest: Partial<RelationsRequest>): Promise<FinishedActionResponse> {
        return this.ajaxService
            .createRequest(`${UserDataService.RELATIONS_ROUTE}`)
            .post(transferify(relationsRequest));
    }

    @ngAsync()
    public async step0registration(request: AnyObject): Promise<ActionResponse> {
        return this.ajaxService
            .createRequest(`${UserDataService.CREDENTIALS_REGISTRATION_FIRST_LOGIN_ROUTE}`)
            .post(request);
    }

    @ngAsync()
    public async uploadUserFiles(request: UploadedUserFilesRequest): Promise<FinishedSaveResponse<string[]>> {
        const formData = new FormData();

        const files = request.files ?? [];
        files.forEach((file) => formData.append('files', file));
        formData.append('user', transferify(request.user, true));

        if (exists(request.accessType)) {
            formData.append('accessType', transferify(request.accessType, true));
        }

        if (exists(request.category)) {
            formData.append('category', transferify(request.category, true));
        }

        return this.ajaxService
            .createRequest(`${UserDataService.UPLOAD_USER_FILES}`)
            .withHeaders({'Content-Type': undefined}) // Browser will set Content-Type automatically
            .post(formData);
    }
}